import { Context, createContext, JSX, useContext, useEffect, useMemo, useState } from 'react';
import { Formik, FormikErrors, FormikTouched } from 'formik';

import { usePendingData } from './PendingData.provider';
import { FieldUtils } from '../hooks/useFields/types';

export function createMultiStepFormContext<T>(useFields: () => FieldUtils<T>): Context<MultiStepFormCtx<T>> {
  return createContext<MultiStepFormCtx<T>>({
    values: {} as T,
    errors: {},
    touched: {},
    useFields,
    handleBlur: () => {},
    handleFieldChange: () => {},
    handleToggleChange: () => {},
    setFieldTouched: () => {},
    setFieldValue: () => {},
  });
}

export function MultiStepFormProvider<T>({
  context,
  disablePendingData,
  onSubmit,
  children,
}: {
  context: Context<MultiStepFormCtx<T>>;
  disablePendingData: boolean;
  onSubmit: (data: T) => void;
  children: JSX.Element;
}): JSX.Element {
  const ctx = useContext(context);
  const { initPendingData, updatePendingData } = usePendingData();
  const { getInitValues, getValidationSchema, getResetSubscriptions } = ctx.useFields();

  const initialFormData = useMemo(() => getInitValues(), []);

  useEffect(() => {
    if (!disablePendingData) {
      initPendingData(initialFormData);
    }
  }, []);

  const resetSubscriptions = useMemo(() => getResetSubscriptions(), []);
  const [multiStepCtx, setMultiStepCtx] = useState(ctx);

  return (
    <Formik
      initialValues={initialFormData}
      validationSchema={getValidationSchema(multiStepCtx)}
      validateOnChange
      validateOnBlur
      onSubmit={onSubmit}
    >
      {({
        errors,
        touched,
        values,
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldTouched,
        setFieldError,
        setFieldValue,
      }) => {
        const handleCommonChange: CommonHandler<T> = (event, dataKey, value) => {
          if (!disablePendingData) {
            updatePendingData({ [dataKey]: value });
          }
          handleChange(event);

          if (String(value) !== String(values[dataKey])) handleResetOnUpdate(dataKey);
        };

        function handleResetOnUpdate(dataKey: keyof T) {
          /*  This is similar to pubsub in that any field that relies on another will be reset when that field changes.
              The filter is not 100% necessary, but performance can be poor without it on fields that have many subscribers. */
          const subscriptions = resetSubscriptions[dataKey] || [];
          const filtered = subscriptions.filter((subscription) => touched[subscription]);
          filtered?.forEach((subscription) => {
            setFieldValue(String(subscription), initialFormData[subscription]);
            setFieldError(String(subscription), undefined);
            setFieldTouched(String(subscription), false);
          });
        }

        function handleToggleChange(
          event: React.ChangeEvent<HTMLInputElement>,
          { id: name, isEnabled }: ToggleChangeProps<T>,
        ) {
          if (isKey(name) && typeof isEnabled === 'boolean') {
            handleCommonChange(event, name, isEnabled);
          }
        }

        function handleFieldChange(event: React.ChangeEvent<HTMLInputElement>) {
          const { name, value } = event.currentTarget;

          if (isKey(name)) {
            /* setting these values for <Select /> use as that component lacks a normal blur (used to set touch values) */
            setFieldValue(name, value);
            handleCommonChange(event, name, value);
          }
        }

        useEffect(() => {
          /*  storing the context in state in order to pass to the validation schema. 
              This allows validate() fns to make use of field values and pass ctx to other fns. */

          setMultiStepCtx({
            values,
            errors,
            touched,
            useFields: ctx.useFields,
            handleBlur,
            handleFieldChange,
            handleToggleChange,
            setFieldTouched,
            setFieldValue,
          });
        }, [values, touched, errors]);

        return (
          <context.Provider value={multiStepCtx}>
            <form onSubmit={handleSubmit}>{children}</form>
          </context.Provider>
        );
      }}
    </Formik>
  );

  function isKey(_name?: string | number | symbol): _name is keyof T {
    return _name ? Object.prototype.hasOwnProperty.call(initialFormData, _name) : false;
  }
}

/* Type Definitions */

export type MultiStepFormCtx<T> = {
  values: T;
  errors: FormikErrors<T>;
  touched: FormikTouched<T>;
  useFields: () => FieldUtils<T>;
  handleBlur: (event: React.FocusEvent<HTMLInputElement>) => void;
  handleFieldChange: (event: React.ChangeEvent<HTMLInputElement>) => void;

  handleToggleChange: (
    event: React.ChangeEvent<HTMLInputElement>,
    props: { id?: keyof T; isEnabled?: boolean },
  ) => void;
  setFieldTouched: (field: string, isTouched?: boolean | undefined, shouldValidate?: boolean | undefined) => void;
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
};

export type ToggleChangeProps<T> = {
  id?: keyof T;
  isEnabled?: boolean;
};

export type CommonHandler<T> = (
  event: React.ChangeEvent<HTMLInputElement>,
  dataKey: keyof T,
  value: string | boolean,
) => void;

export type ToggleHandler<T> = (event: React.ChangeEvent<HTMLInputElement>, props: ToggleChangeProps<T>) => void;
